// S37.1: a finished row folded into its notice (on a card table, its slim row), the actions and answers that
// fold, restore, remove and re-rank rows, and the toasts, whose Undo puts a Move to topic back (P5, D101).

import { ROWS_EVENT } from "../insights/activity-summary.js";
import { send } from "../insights/writes.js";

// The Insights toast's event (components/insights/toast.blade.php), and what its Undo sends back to the
// table, {table, kind, ids} (undoFromToast()).
const TOAST_EVENT = "insights-toast";
export const UNDO_EVENT = "activity-table-undo";

/** Whether a modal dialog (a drawer, a dialog: aria-modal) is on screen over the list. */
export function isUnderDialog(root = typeof document === "undefined" ? null : document) {
    if (root === null || typeof root.querySelectorAll !== "function") {
        return false;
    }

    return [...root.querySelectorAll('[aria-modal="true"]')].some((dialog) => typeof dialog.getClientRects === "function" && dialog.getClientRects().length > 0);
}

export default function slimRowPart({ ACTION_EVENT, HOST_METHOD, MOVE_ACTION }) {
    return {
        noticeOf(id) {
            return this.notices[id] || null;
        },

        // Rows the activity sent folded (D70): finished earlier in this visit and sent again in a contract it
        // built afresh, each drawn folded where it was, with its Undo, and with its notice (S43.1) or what
        // finished it (S37.1).
        seedFolded() {
            this.rows.forEach((row) => {
                if (row.folded) {
                    this.notices[row.id] = this.foldNotice(String(row.folded.notice || ""), row.folded.undo === true, row.folded.kind);
                }
            });
        },

        // A fold's notice: its line, whether its Undo is offered, and what finished it where the activity
        // names that (ActivityTable::FOLDED_KINDS), since a card table's slim row then reads that kind's own
        // sentence. A fold that names none, a move in Sort my searches (S43.1) or a copy's (S45.2), reads
        // its own line.
        foldNotice(text, undo, kind) {
            return kind === "done" || kind === "not_relevant" ? { text, undo, kind } : { text, undo };
        },

        // What a slim row's icon follows: a fold that names no kind takes Done's tick, as the notice it
        // replaces did.
        noticeKindOf(id) {
            const notice = this.noticeOf(id);

            return notice ? notice.kind || "done" : "done";
        },

        // A card table's slim row reads its kind's sentence; a fold that names no kind reads its own line.
        noticeSentenceOf(id) {
            const notice = this.noticeOf(id);

            return notice && !notice.kind && notice.text !== "" ? notice.text : null;
        },

        isPending(id) {
            return this.pending[id] === true;
        },

        // ------------------------------------------------------------------------------- actions

        actOnRow(action, id) {
            const index = this.visibleRows.findIndex((row) => row.id === id);

            return this.act(action, [id], { kind: "row", id, action, index });
        },

        undo(id) {
            const index = this.visibleRows.findIndex((row) => row.id === id);

            return this.act("undo", [id], { kind: "notice", id, index });
        },

        // The toast's Undo (UNDO_EVENT, {table, kind, ids}): the activity puts back the write `kind` names
        // (a Move to topic). Every table hears it, so only the one it names acts. The toast that held
        // focus is gone, so focus goes to the row put back, or to the count when there were several.
        undoFromToast(detail) {
            if (!detail || detail.table !== this.table.id || !Array.isArray(detail.ids) || detail.ids.length === 0) {
                return;
            }

            const id = detail.ids[0];
            const origin = detail.ids.length === 1 ? { kind: "row", id, action: MOVE_ACTION, index: this.visibleRows.findIndex((row) => row.id === id) } : { kind: "bulk" };

            return this.act("undo", detail.ids, origin, detail.kind);
        },

        // A notice the reader closes takes its row out of the list: the activity already did the work, so
        // its fold is kept for the summary, which still counts the row done (tellRows()).
        closeNotice(id) {
            const index = this.visibleRows.findIndex((row) => row.id === id);
            const notice = this.noticeOf(id);

            if (notice) {
                this.closedFolds[id] = { kind: notice.kind, undo: notice.undo === true };
            }

            this.dropRows(new Set([id]));
            this.refresh();
            this.afterShow(() => this.focusRow(index, null));
        },

        // `value` is what the action carries beyond its rows: the topic Move to topic picked, or an Undo's
        // kind. Undefined for every other action, which then calls the host with three arguments.
        async act(action, ids, origin, value = undefined) {
            const rows = ids.map((id) => this.rowById(id)).filter((row) => row !== null);

            if (rows.length === 0 || rows.some((row) => this.pending[row.id]) || (origin.kind === "bulk" && this.isBulkPending)) {
                return;
            }

            // Move to topic asks for its topic first, in the table's own dialog, when the table sends its
            // choices (P5, D101); the pick comes back here with the topic as its value.
            if (action === MOVE_ACTION && value === undefined && this.hasMoveDialog()) {
                this.openMoveDialog(rows.map((row) => row.id), origin);

                return;
            }

            // `origin` says where the press came from (bulk, row or notice), so a host that answers in
            // the browser and sends the press back can repeat it exactly: focus cannot tell it, since
            // Safari and Firefox on a Mac never focus a clicked button.
            const event = new CustomEvent(ACTION_EVENT, {
                bubbles: true,
                cancelable: true,
                detail: { table: this.table.id, action, ids: rows.map((row) => row.id), rows, value, origin: origin.kind },
            });

            if (!this.rootElement.dispatchEvent(event)) {
                return;
            }

            this.error = "";
            rows.forEach((row) => {
                this.pending[row.id] = true;
            });
            this.isBulkPending = origin.kind === "bulk";

            // The page the press was made on, which focus may return to only while the reader is on it.
            const pressed = { ...origin, pressedOnPage: this.currentPage };
            let answer;

            try {
                answer = await this.callHost(action, rows.map((row) => row.id), value);
            } catch (error) {
                answer = { error: this.strings.failed };
            }

            rows.forEach((row) => {
                delete this.pending[row.id];
            });
            this.isBulkPending = false;
            this.apply(answer && typeof answer === "object" ? answer : {}, pressed);
        },

        // Every action a press sends goes through the writes module (P1, insights/writes.js), which today
        // calls the activity's own host method and tells the page's other screens once it saved.
        callHost(action, ids, value = undefined) {
            return send({
                el: this.rootElement,
                action,
                payload: { table: this.table.id, ids, value },
                livewire: () => this.callWire(HOST_METHOD, value === undefined ? [this.table.id, action, ids] : [this.table.id, action, ids, value]),
            });
        },

        // The activity's answer: {replaced, removed, finished: {ids, notice, undo}, restored, updated,
        // announce, error}. Only ids still in the table count, and an error applies nothing else.
        apply(answer, origin) {
            if (typeof answer.error === "string" && answer.error !== "") {
                this.error = answer.error;
                this.alert(answer.error);

                return;
            }

            // An answer from elsewhere while a dialog covers the list (Draft an article's copy folding its row) is
            // drawn at once: a view transition under a dialog moves nothing anyone watches, holds the page's old
            // picture while it runs, and a dialog that closes inside it sits frozen, then jumps (P83, 22 Oct).
            if (origin.kind === "elsewhere" && isUnderDialog()) {
                this.applyRows(answer, origin);

                return;
            }

            this.moveRowsWith(() => this.applyRows(answer, origin));
        },

        // The row half of an answer, inside the list's motion: rows re-ranked, changed, folded, back or gone.
        // A view transition runs this a frame after the answer lands, so a page turned since the press is
        // read here, not when the answer lands.
        applyRows(answer, origin) {
            const isPageTurned = origin.pressedOnPage !== undefined && origin.pressedOnPage !== this.currentPage;

            if (Array.isArray(answer.replaced)) {
                this.replaceRows(answer.replaced);
            }

            const known = (ids) => (Array.isArray(ids) ? ids.filter((id) => this.rowById(id) !== null) : []);

            (Array.isArray(answer.updated) ? answer.updated : []).forEach((row) => {
                const index = row && typeof row === "object" ? this.rows.findIndex((candidate) => candidate.id === row.id) : -1;

                // The row's other keys stay as they were (its advice, a card row's rank and raw score).
                if (index !== -1) {
                    this.rows.splice(index, 1, {
                        ...this.rows[index],
                        ...row,
                        id: row.id,
                        cells: { ...this.rows[index].cells, ...(row.cells || {}) },
                        stage: row.stage ?? null,
                        topicId: row.topicId ?? null,
                        isUnsafe: row.isUnsafe === true,
                        actions: Array.isArray(row.actions) ? row.actions : [],
                        detail: row.detail === undefined ? this.rows[index].detail ?? null : keepAbove(this.rows[index].detail, row.detail),
                        isNew: row.isNew === true,
                    });
                }
            });

            const finished = answer.finished && typeof answer.finished === "object" ? answer.finished : null;
            const finishedIds = finished ? known(finished.ids) : [];
            // `undo` is true or false for every row it folded, or the ids that get one: a bulk Done offers
            // Undo only on the targets that press created.
            const undoIds = finished && Array.isArray(finished.undo) ? new Set(finished.undo) : null;

            // A row that folds closes its detail (Reine, 26 Oct): its Undo brings it back shut, rather than drawing the
            // detail it hid afresh as it unfolds; the address stops naming it as the open row.
            const foldedOpen = finishedIds.some((id) => this.expanded[id] === true);

            finishedIds.forEach((id) => {
                this.notices[id] = this.foldNotice(String(finished.notice || ""), undoIds ? undoIds.has(id) : finished.undo === true, finished.kind);
                delete this.selected[id];
                delete this.expanded[id];
            });

            if (foldedOpen) {
                this.tellOpenRow();
            }

            // The toast's Undo of a write that left the rows in the list (a Move to topic): {kind, ids}.
            const toastUndo = answer.toastUndo && typeof answer.toastUndo === "object" && Array.isArray(answer.toastUndo.ids) ? answer.toastUndo : null;
            const restoredIds = known(answer.restored);

            restoredIds.forEach((id) => {
                delete this.notices[id];
            });

            const removed = new Set(known(answer.removed));

            this.dropRows(removed);
            this.refresh();
            this.tellRows();

            // An answer from elsewhere on the page (receiveAnswer()) changes the rows and nothing else, but for the
            // focus a fold takes away: Draft an article's copy can fold its row after the drawer has closed and given
            // focus back to the row's button, which the fold then hides (P83). That focus goes to the row's Undo.
            if (origin.kind === "elsewhere") {
                this.keepFocusInFolds(finishedIds);

                return;
            }

            // `announce: false` says nothing: the activity says it itself (Sort my searches' toast, S43.1).
            const spoken = answer.announce === false ? "" : typeof answer.announce === "string" && answer.announce !== "" ? answer.announce : finished ? String(finished.notice || "") : "";

            if (spoken !== "") {
                this.say(spoken, toastUndo);
            }

            // A page turned since the press keeps the reader there, with no focus step. Otherwise the step
            // carries the page this answer leaves the reader on, so a page turned while the rows still move
            // stops it too (restoreFocus()).
            if (isPageTurned) {
                return;
            }

            const page = this.currentPage;

            this.afterShow(() => this.restoreFocus({ ...origin, page }, { removed, finishedIds, restoredIds }));
        },

        // The rows changed, by a press here or an answer from elsewhere (the writes module's routes among
        // them, which no server summary follows): whoever draws figures from them works them out again (the
        // activity's summary panel, insights/activity-summary.js). The rows go as they are, with each fold,
        // and the folds of notices closed this visit.
        tellRows() {
            if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") {
                return;
            }

            window.dispatchEvent(new CustomEvent(ROWS_EVENT, { detail: { table: this.table.id, rows: this.rows, notices: this.notices, closed: this.closedFolds } }));
        },

        // A card table says what happened in the Insights toast, which is the page's status region and,
        // for a Move to topic, carries its Undo (D71, D101); every other table says it in its own live
        // region.
        say(text, toastUndo = null) {
            if (!this.isCard() || typeof window === "undefined") {
                this.announce(text);

                return;
            }

            const detail = { message: text };

            if (toastUndo !== null) {
                detail.undo = { event: UNDO_EVENT, detail: { table: this.table.id, kind: toastUndo.kind, ids: toastUndo.ids } };
            }

            window.dispatchEvent(new CustomEvent(TOAST_EVENT, { detail }));
        },

        // Rows that fold, come back or re-rank move into place (x-list-motion on the table, D58, D74):
        // the change is made inside $moveRows, after which Alpine draws it on the next tick.
        moveRowsWith(update) {
            if (typeof this.$moveRows !== "function") {
                update();

                return;
            }

            this.$moveRows(async () => {
                update();
                await this.$nextTick();
            });
        },

        dropRows(ids) {
            if (ids.size === 0) {
                return;
            }

            // A row that leaves while open is no longer the row the address names.
            const wasOpen = [...ids].some((id) => this.expanded[id] === true);

            this.rows = this.rows.filter((row) => !ids.has(row.id));
            ids.forEach((id) => {
                delete this.selected[id];
                delete this.notices[id];
                delete this.revealed[id];
                delete this.pending[id];
                delete this.expanded[id];
                delete this.trends[id];

                if (this.menuFor === id) {
                    this.menuFor = null;
                }
            });

            if (wasOpen) {
                this.tellOpenRow();
            }
        },

        // A list the activity ranked again (a stage changed, D53): its rows in their new order. The
        // reader's sort, filters, selection, open rows, trends and notices stay with every row still
        // in it, and go with the rows that left.
        replaceRows(rows) {
            // Every key a row carries comes along (its advice for Draft an article, a card row's rank,
            // raw score and facts), with today's defaults where the answer left one out. A row's
            // Above you, which its first open answered, stays with it (B3): the ranking reads no Google
            // results, and a row that lost it drew none until it opened again.
            const next = rows
                .filter((row) => row && typeof row === "object" && typeof row.id === "string")
                .map((row) => ({
                    ...row,
                    cells: row.cells || {},
                    stage: row.stage ?? null,
                    topicId: row.topicId ?? null,
                    isUnsafe: row.isUnsafe === true,
                    actions: Array.isArray(row.actions) ? row.actions : [],
                    detail: keepAbove(this.rowById(row.id)?.detail, row.detail ?? null),
                    isNew: row.isNew === true,
                }));
            const kept = new Set(next.map((row) => row.id));

            this.dropRows(new Set(this.rows.filter((row) => !kept.has(row.id)).map((row) => row.id)));
            this.rows = next;
            this.order = {};
            next.forEach((row, index) => {
                this.order[row.id] = index;
            });
        },

        // Focus never falls to the page (accessibility.md §10): a gone row hands it to the row that took
        // its place, a notice takes it on its Undo, a row back from its notice takes it on its first
        // button, and the bulk bar, once nothing is selected, hands it to the count above the table. A
        // reader who turned the page while the answer was on its way, or while its rows still moved, keeps
        // the focus the new page gave them, and is never pulled back to the page they left.
        restoreFocus(origin, outcome) {
            if (origin.page !== undefined && origin.page !== this.currentPage) {
                return;
            }

            if (origin.kind === "bulk") {
                if (this.selectedCount() === 0) {
                    this.focusSummary();
                }

                return;
            }

            if (outcome.removed.has(origin.id) || (origin.kind === "row" && this.rowById(origin.id) === null)) {
                this.focusRow(origin.index, origin.action || null);

                return;
            }

            // A row the answer moved to another page (a list ranked again, a sort on a figure that changed)
            // is followed there, once its page is drawn, so focus and its open row go with it.
            if (!this.isOnPage(origin.id) && this.showPageOf(origin.id)) {
                this.afterShow(() => this.restoreFocus({ ...origin, page: this.currentPage }, outcome));

                return;
            }

            const row = this.rowElement(origin.id);

            // A move, or its Undo: the row may have moved in the list, and focus goes with it to its ⋯,
            // where the move was pressed.
            if (origin.action === MOVE_ACTION) {
                if (row) {
                    this.focusWithin(row, MOVE_ACTION);
                } else {
                    this.focusSummary();
                }

                return;
            }

            if (row && (outcome.finishedIds.includes(origin.id) || outcome.restoredIds.includes(origin.id))) {
                this.focusWithin(row, null);
            }
        },

        // Focus inside a row this answer folded (the button a fold hides) moves to its notice's Undo, else its
        // Close, once the notice is drawn, so it never falls to the page. Focus anywhere else stays where it is.
        keepFocusInFolds(ids) {
            const active = typeof document === "undefined" ? null : document.activeElement;

            if (!active || ids.length === 0 || typeof this.rootElement?.querySelectorAll !== "function") {
                return;
            }

            const row = ids.map((id) => this.rowElement(id)).find((element) => element !== null && typeof element.contains === "function" && element.contains(active));

            if (row) {
                this.afterShow(() => this.focusWithin(row, null));
            }
        },

        rowElements() {
            return Array.from(this.rootElement.querySelectorAll("tr[data-row]"));
        },

        rowElement(id) {
            return this.rowElements().find((element) => element.dataset.rowId === id) || null;
        },

        // The row now at `index` (the next one), else the last one, else the count above the table.
        focusRow(index, action) {
            const rows = this.rowElements();
            const row = rows[Math.min(index, rows.length - 1)] || null;

            if (row) {
                this.focusWithin(row, action);
            } else {
                this.focusSummary();
            }
        },

        // A notice takes focus on its Undo, or its Close when it has none. A row takes it on the button
        // that was pressed, so a reader pressing Draft an article down a list keeps pressing it; on its ⋯
        // for an action that waits there (P5, D98); else on its button, its ⋯, its disclosure or its header.
        focusWithin(row, action) {
            const id = row.dataset.rowId;
            const notice = this.noticeOf(id);
            const data = this.rowById(id);
            const scope = row.closest?.("tbody") || row;
            let target = null;

            if (notice) {
                target = row.querySelector(notice.undo ? "[data-notice-undo]" : "[data-notice-close]");
            } else {
                if (action && data && this.menuActionsOf(data).includes(action)) {
                    target = this.rowTriggerOf(id);
                }

                target = target || (action && scope.querySelector(`[data-row-action="${action}"]`)) || scope.querySelector("[data-row-action]") || this.rowTriggerOf(id) || row.querySelector("[data-row-expand]") || row.querySelector("[data-row-header]");
            }

            if (target) {
                target.focus();
            }
        },

        focusSummary() {
            const summary = this.rootElement.querySelector("[data-summary]");

            if (summary) {
                summary.focus();
            }
        },
    };
}

// A new detail for a row, keeping the Above you its old detail held when the new one has none (B3).
function keepAbove(previous, next) {
    if (!next || typeof next !== "object" || next.above !== undefined || !previous || previous.above === undefined) {
        return next;
    }

    return { ...next, above: previous.above };
}
